import ReactECharts from "echarts-for-react";
import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

import "./SvuotamentiSettAtersir.css";
import RotateDevice from "../components/RotateDevice/RotateDevice";
import Lf23Loader from "../components/Lf23Loader";

const getWeekDates = (anno, settimana) => {
    const jan4 = new Date(anno, 0, 4);
    const dow = jan4.getDay() || 7;
    const mondayW1 = new Date(jan4);
    mondayW1.setDate(jan4.getDate() - dow + 1);
    const monday = new Date(mondayW1);
    monday.setDate(mondayW1.getDate() + (settimana - 1) * 7);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const fmt = (d) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
    return `${fmt(monday)} - ${fmt(sunday)}`;
};

const useCountUp = (target, decimals = 0, duration = 900) => {
    const [current, setCurrent] = useState(0);
    useEffect(() => {
        const startTime = performance.now();
        const animate = (now) => {
            const progress = Math.min((now - startTime) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            const value = target * eased;
            setCurrent(decimals > 0 ? parseFloat(value.toFixed(decimals)) : Math.round(value));
            if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
    }, [target, decimals, duration]);
    return current;
};

const SvuotamentiSettAtersir = () => {
    const navigate = useNavigate();

    const [showFilters, setShowFilters] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isFullscreenChart, setIsFullscreenChart] = useState(false);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                if (isFullscreen) setIsFullscreen(false);
                if (isFullscreenChart) setIsFullscreenChart(false);
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isFullscreen, isFullscreenChart]);

    const defaultAnno = [{ value: new Date().getFullYear(), label: String(new Date().getFullYear()) }];
    const [filterAnno, setFilterAnno] = useState(defaultAnno);
    const [filterSettimane, setFilterSettimane] = useState([]);
    const [filterProvince, setFilterProvince] = useState([]);
    const [filterComuni, setFilterComuni] = useState([]);

    const [opzioniProvince, setOpzioniProvince] = useState([]);
    const [opzioniComuni, setOpzioniComuni] = useState([]);
    const [opzioniSettimane, setOpzioniSettimane] = useState([]);

    const [kpiData, setKpiData] = useState({
        svuotamentiEffettuati: 0,
        targetAtersir: 0,
        coperturaMediaPerc: 0,
        comuniMonitorati: 0,
        province: 0
    });

    const animSvuotamenti = useCountUp(kpiData.svuotamentiEffettuati);
    const animTarget = useCountUp(kpiData.targetAtersir);
    const animCopertura = useCountUp(kpiData.coperturaMediaPerc, 1);
    const animComuni = useCountUp(kpiData.comuniMonitorati);
    const animProvince = useCountUp(kpiData.province);

    const [chartData, setChartData] = useState([]);
    const [chartLoading, setChartLoading] = useState(true);

    const [tableData, setTableData] = useState([]);
    const [tableLoading, setTableLoading] = useState(true);
    const [paginaCorrente, setPaginaCorrente] = useState(1);
    const [pagineTotali, setPagineTotali] = useState(1);
    const [totaleRighe, setTotaleRighe] = useState(0);
    const [totaleComuni, setTotaleComuni] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [tableMode, setTableMode] = useState("settimanale");
    const [appliedTableMode, setAppliedTableMode] = useState("settimanale");
    const [displayedTableMode, setDisplayedTableMode] = useState("settimanale");
    const [soloSottoTarget, setSoloSottoTarget] = useState(false);

    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const res = await axios.get(`${API_URL}/svuotamenti-sett-atersir/filters`);
                setOpzioniProvince(res.data.provincia.filter(p => p.valore !== "Extra").map(p => ({ value: p.valore, label: p.valore })));
                setOpzioniComuni(res.data.comune.filter(c => !c.valore.startsWith("EXTRA")).map(c => ({ value: c.valore, label: c.valore })));
            } catch (error) {
                console.error("Errore nel caricamento filtri:", error);
            }
        };
        fetchFilters();
    }, [API_URL]);

    useEffect(() => {
        const fetchSettimane = async () => {
            try {
                const res = await axios.get(`${API_URL}/svuotamenti-sett-atersir/settimane?anno=${filterAnno.map(a => a.value).join(",")}`);
                const multiYear = filterAnno.length > 1;
                const grouped = res.data.map(group => ({
                    label: multiYear ? `${group.mese} ${group.anno}` : group.mese,
                    options: group.settimane.map(s => ({
                        value: s,
                        label: `Sett. ${s}`,
                        dateRange: getWeekDates(group.anno, s)
                    }))
                }));
                setOpzioniSettimane(grouped);
                setFilterSettimane([]);
            } catch (error) {
                console.error("Errore nel caricamento settimane:", error);
            }
        };
        fetchSettimane();
    }, [API_URL, filterAnno]);

    const [appliedFilters, setAppliedFilters] = useState({
        anno: defaultAnno,
        settimane: [],
        province: [],
        comuni: []
    });

    const handleApply = useCallback(() => {
        setAppliedFilters({
            anno: filterAnno,
            settimane: filterSettimane,
            province: filterProvince,
            comuni: filterComuni
        });
        setAppliedTableMode(tableMode);
        setPaginaCorrente(1);
    }, [filterAnno, filterSettimane, filterProvince, filterComuni, tableMode]);

    const handleReset = useCallback(() => {
        setFilterAnno(defaultAnno);
        setFilterSettimane([]);
        setFilterProvince([]);
        setFilterComuni([]);
        setTableMode("settimanale");
        setAppliedFilters({
            anno: defaultAnno,
            settimane: [],
            province: [],
            comuni: []
        });
        setAppliedTableMode("settimanale");
        setDisplayedTableMode("settimanale");
        setSoloSottoTarget(false);
    }, [defaultAnno]);

    useEffect(() => {
        const fetchKpi = async () => {
            try {
                const params = new URLSearchParams();
                if (appliedFilters.anno.length > 0) {
                    params.append("anno", appliedFilters.anno.map(a => a.value).join(","));
                }
                if (appliedFilters.settimane.length > 0) {
                    params.append("settimane", appliedFilters.settimane.map(s => s.value).join(","));
                }
                if (appliedFilters.province.length > 0) {
                    params.append("province", appliedFilters.province.map(p => p.value).join(","));
                }
                if (appliedFilters.comuni.length > 0) {
                    params.append("comuni", appliedFilters.comuni.map(c => c.value).join(","));
                }
                params.append("frequenza", appliedTableMode);
                const res = await axios.get(`${API_URL}/svuotamenti-sett-atersir/kpi?${params.toString()}`);
                setKpiData(res.data);
            } catch (error) {
                console.error("Errore nel caricamento KPI:", error);
            }
        };
        fetchKpi();
    }, [API_URL, appliedFilters, appliedTableMode]);

    useEffect(() => {
        const fetchChart = async () => {
            setChartLoading(true);
            try {
                const params = new URLSearchParams();
                if (appliedFilters.anno.length > 0) {
                    params.append("anno", appliedFilters.anno.map(a => a.value).join(","));
                }
                if (appliedFilters.settimane.length > 0) {
                    params.append("settimane", appliedFilters.settimane.map(s => s.value).join(","));
                }
                if (appliedFilters.province.length > 0) {
                    params.append("province", appliedFilters.province.map(p => p.value).join(","));
                }
                if (appliedFilters.comuni.length > 0) {
                    params.append("comuni", appliedFilters.comuni.map(c => c.value).join(","));
                }
                params.append("frequenza", appliedTableMode);
                const res = await axios.get(`${API_URL}/svuotamenti-sett-atersir/chart?${params.toString()}`);
                setChartData(res.data);
            } catch (error) {
                console.error("Errore nel caricamento chart:", error);
            } finally {
                setChartLoading(false);
            }
        };
        fetchChart();
    }, [API_URL, appliedFilters, appliedTableMode]);

    useEffect(() => {
        const fetchTable = async () => {
            setTableLoading(true);
            try {
                const params = new URLSearchParams();
                if (appliedFilters.anno.length > 0) {
                    params.append("anno", appliedFilters.anno.map(a => a.value).join(","));
                }
                if (appliedFilters.settimane.length > 0) {
                    params.append("settimane", appliedFilters.settimane.map(s => s.value).join(","));
                }
                if (appliedFilters.province.length > 0) {
                    params.append("province", appliedFilters.province.map(p => p.value).join(","));
                }
                if (appliedFilters.comuni.length > 0) {
                    params.append("comuni", appliedFilters.comuni.map(c => c.value).join(","));
                }
                params.append("page", paginaCorrente);
                params.append("limit", rowsPerPage);
                params.append("frequenza", appliedTableMode);
                if (soloSottoTarget) params.append("soloSottoTarget", "true");
                const res = await axios.get(`${API_URL}/svuotamenti-sett-atersir/table?${params.toString()}`);
                setTableData(res.data.data);
                setDisplayedTableMode(appliedTableMode);
                setPagineTotali(res.data.pagination.totalPages);
                setTotaleRighe(res.data.pagination.totalItems);
                setTotaleComuni(res.data.totaleComuni);
            } catch (error) {
                console.error("Errore nel caricamento tabella:", error);
                setTableData([]);
            } finally {
                setTableLoading(false);
            }
        };
        fetchTable();
    }, [API_URL, appliedFilters, paginaCorrente, appliedTableMode, rowsPerPage, soloSottoTarget]);

    const handlePageChange = (nuovaPagina) => {
        if (nuovaPagina >= 1 && nuovaPagina <= pagineTotali) {
            setPaginaCorrente(nuovaPagina);
        }
    };

    const getCoperturaColor = (perc) => {
        if (perc < 90) return "#EF4444";
        if (perc < 100) return "#F59E0B";
        return "#10B981";
    };

    const chartOptions = {
        tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow" }
        },
        legend: {
            data: ["Copertura %", "Comuni sotto target"],
            top: 0,
            right: 0
        },
        grid: {
            left: "3%",
            right: "4%",
            bottom: "3%",
            containLabel: true
        },
        xAxis: {
            type: "category",
            data: chartData.map(d => `${d.settimanaLabel} - ${d.anno}`),
            axisLabel: { fontSize: 11, color: "#64748B" }
        },
        yAxis: [
            {
                type: "value",
                name: "% Copertura",
                position: "left",
                axisLabel: { formatter: "{value}%", fontSize: 11, color: "#64748B" }
            },
            {
                type: "value",
                name: "N. Comuni",
                position: "right",
                minInterval: 1,
                axisLabel: { fontSize: 11, color: "#64748B" }
            }
        ],
        series: [
            {
                name: "Copertura %",
                type: "bar",
                yAxisIndex: 0,
                data: chartData.map(d => d.coperturaPerc),
                itemStyle: { color: "#F59E0B", borderRadius: [4, 4, 0, 0] },
                barMaxWidth: 40,
                label: {
                    show: true,
                    position: "top",
                    formatter: "{c}%",
                    fontSize: 10,
                    fontWeight: 600,
                    color: "#F59E0B"
                }
            },
            {
                name: "Comuni sotto target",
                type: "bar",
                yAxisIndex: 1,
                data: chartData.map(d => d.comuniSottoTarget),
                itemStyle: { color: "#EF4444", borderRadius: [4, 4, 0, 0] },
                barMaxWidth: 40,
                label: {
                    show: true,
                    position: "top",
                    formatter: "{c}",
                    fontSize: 10,
                    fontWeight: 600,
                    color: "#EF4444"
                }
            }
        ]
    };

    return (
        <div className="svuot-sett-container">
            {/* Header */}
            <header className="page-header">
                <div className="header-content">
                    <button className="back-button" onClick={() => navigate("/produttivita")}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        Indietro
                    </button>
                    <div className="header-title">
                        <div className="header-title-row">
                            <h2>Svuotamenti Settimanali ATERSIR</h2>
                            <button
                                className="info-button-modern"
                                onClick={() => setShowInfoModal(true)}
                                title="Informazioni"
                            >
                                <span className="info-icon-text">i</span>
                            </button>
                        </div>
                        <p className="header-subtitle">Monitoraggio copertura svuotamenti rispetto agli obiettivi ATERSIR per comune e settimana</p>
                    </div>
                </div>
            </header>

            {/* Modal Info */}
            {showInfoModal && (
                <div className="modal-overlay" onClick={() => setShowInfoModal(false)}>
                    <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-icon">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                                </svg>
                            </div>
                            <h3>Informazioni sulla pagina</h3>
                            <button className="modal-close" onClick={() => setShowInfoModal(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="modal-section">
                                <h4>Cosa fa questa pagina?</h4>
                                <p>
                                    Questa dashboard mostra la <strong>copertura settimanale degli svuotamenti</strong> rispetto
                                    agli obiettivi definiti da ATERSIR, suddivisi per comune e settimana.
                                </p>
                            </div>
                            <div className="modal-section">
                                <h4>Come leggere i dati</h4>
                                <ul>
                                    <li><strong>Comune:</strong> Il comune di riferimento per gli svuotamenti</li>
                                    <li><strong>Settimana:</strong> Il periodo settimanale di analisi</li>
                                    <li><strong>Obiettivo ATERSIR:</strong> Il numero di svuotamenti previsti</li>
                                    <li><strong>Svuotamenti effettuati:</strong> Il numero reale di svuotamenti eseguiti</li>
                                    <li><strong>Copertura %:</strong> Rapporto tra effettuati e obiettivo</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Content Area */}
            <div className="content-area">
                {/* Top Controls */}
                <div className="top-controls">
                    <div className="controls-left">
                        <button
                            className={`toggle-filters-btn ${showFilters ? 'active' : ''}`}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                            </svg>
                            {showFilters ? 'Nascondi Filtri' : 'Mostra Filtri'}
                        </button>
                    </div>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="filters-panel-svuot">
                        <div className="filters-grid-svuot">
                            {/* ANNO */}
                            <div className="filter-item-svuot">
                                <label>Anno</label>
                                <Select
                                    isMulti
                                    classNamePrefix="rs"
                                    options={[
                                        { value: 2025, label: "2025" },
                                        { value: 2026, label: "2026" }
                                    ]}
                                    value={filterAnno}
                                    onChange={(selected) => setFilterAnno(selected || [])}
                                    placeholder="Seleziona anno..."
                                    hideSelectedOptions={false}
                                    closeMenuOnSelect={false}
                                />
                            </div>

                            {/* SETTIMANE */}
                            <div className="filter-item-svuot">
                                <label>Settimane</label>
                                <Select
                                    isMulti
                                    classNamePrefix="rs"
                                    options={opzioniSettimane}
                                    value={filterSettimane}
                                    onChange={(selected) => setFilterSettimane(selected || [])}
                                    placeholder="Tutte le settimane..."
                                    hideSelectedOptions={false}
                                    closeMenuOnSelect={false}
                                    formatOptionLabel={(opt) => (
                                        <span>{opt.label} {opt.dateRange && <span style={{ fontWeight: 400, color: '#94A3B8', fontSize: '12px' }}>({opt.dateRange})</span>}</span>
                                    )}
                                />
                            </div>

                            {/* PROVINCIA */}
                            <div className="filter-item-svuot">
                                <label>Provincia</label>
                                <Select
                                    isMulti
                                    classNamePrefix="rs"
                                    options={opzioniProvince}
                                    value={filterProvince}
                                    onChange={(selected) => setFilterProvince(selected || [])}
                                    placeholder="Tutte le province..."
                                    hideSelectedOptions={false}
                                    closeMenuOnSelect={false}
                                />
                            </div>

                            {/* COMUNE */}
                            <div className="filter-item-svuot">
                                <label>Comune</label>
                                <Select
                                    isMulti
                                    classNamePrefix="rs"
                                    options={opzioniComuni}
                                    value={filterComuni}
                                    onChange={(selected) => setFilterComuni(selected || [])}
                                    placeholder="Tutti i comuni..."
                                    hideSelectedOptions={false}
                                    closeMenuOnSelect={false}
                                />
                            </div>

                            <div className="toggle-aggregazione-svuot">
                                <button
                                    className={`toggle-btn-svuot ${tableMode === "settimanale" ? "active" : ""}`}
                                    onClick={() => setTableMode("settimanale")}
                                >
                                    Settimanale
                                </button>
                                <button
                                    className={`toggle-btn-svuot ${tableMode === "bisettimanale" ? "active" : ""}`}
                                    onClick={() => setTableMode("bisettimanale")}
                                >
                                    Bisettimanale
                                </button>
                            </div>

                            {/* BOTTONI */}
                            <div className="filter-item-svuot filter-actions-svuot">
                                <label>&nbsp;</label>
                                <div className="filter-buttons-svuot">
                                    <button type="button" className="btn-apply-svuot" onClick={handleApply}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="20 6 9 17 4 12"></polyline>
                                        </svg>
                                        Applica
                                    </button>
                                    <button type="button" className="btn-reset-svuot" onClick={handleReset}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                                            <path d="M3 3v5h5"></path>
                                        </svg>
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>

                    </div>
                )}
                {/* KPI Cards */}
                <div className="kpi-strip-svuot">
                    <div className="kpi-card-svuot kpi-blue">
                        <div className="kpi-icon-svuot">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                            </svg>
                        </div>
                        <div className="kpi-info-svuot">
                            <span className="kpi-value-svuot">{animSvuotamenti.toLocaleString("it-IT")}</span>
                            <span className="kpi-label-svuot">SVUOTAMENTI EFFETTUATI</span>
                        </div>
                    </div>

                    <div className="kpi-card-svuot kpi-green">
                        <div className="kpi-icon-svuot">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                            </svg>
                        </div>
                        <div className="kpi-info-svuot">
                            <span className="kpi-value-svuot">{animTarget.toLocaleString("it-IT")}</span>
                            <span className="kpi-label-svuot">TARGET ATERSIR</span>
                        </div>
                    </div>

                    <div className="kpi-card-svuot kpi-yellow">
                        <div className="kpi-icon-svuot">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <polyline points="12 6 12 12 16 14"></polyline>
                            </svg>
                        </div>
                        <div className="kpi-info-svuot">
                            <span className="kpi-value-svuot">{animCopertura}%</span>
                            <span className="kpi-label-svuot">COPERTURA MEDIA {appliedFilters.anno.map(a => a.value).join("-")}</span>
                        </div>
                    </div>

                    <div className="kpi-card-svuot kpi-purple">
                        <div className="kpi-icon-svuot">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                <circle cx="12" cy="10" r="3"></circle>
                            </svg>
                        </div>
                        <div className="kpi-info-svuot">
                            <span className="kpi-value-svuot">{animComuni}</span>
                            <span className="kpi-label-svuot">COMUNI MONITORATI</span>
                        </div>
                    </div>

                    <div className="kpi-card-svuot kpi-pink">
                        <div className="kpi-icon-svuot">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                <line x1="3" y1="9" x2="21" y2="9"></line>
                                <line x1="9" y1="21" x2="9" y2="9"></line>
                            </svg>
                        </div>
                        <div className="kpi-info-svuot">
                            <span className="kpi-value-svuot">{animProvince}</span>
                            <span className="kpi-label-svuot">PROVINCE</span>
                        </div>
                    </div>
                </div>

                {/* Loader unico per caricamento iniziale e filtri */}
                {chartLoading ? (
                    <Lf23Loader />
                ) : (
                    <>
                        {/* Grafico Settimanale */}
                        {chartData.length === 0 ? (
                            <div className="oper-hr-empty-state">
                                <div className="oper-hr-empty-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                        <line x1="9" y1="9" x2="15" y2="15"></line>
                                        <line x1="15" y1="9" x2="9" y2="15"></line>
                                    </svg>
                                </div>
                                <h4 className="oper-hr-empty-title">Nessun dato disponibile</h4>
                                <p className="oper-hr-empty-text">Non ci sono dati per i filtri selezionati. Prova a modificare i filtri.</p>
                            </div>
                        ) : (
                            <div className={`chart-container-svuot${isFullscreenChart ? ' fullscreen-chart-svuot' : ''}`}>
                                <RotateDevice active={isFullscreenChart} />
                                <div className="fullscreen-bar-inline-svuot">
                                    <button
                                        className="fullscreen-btn-svuot"
                                        onClick={() => setIsFullscreenChart(!isFullscreenChart)}
                                        title={isFullscreenChart ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            {isFullscreenChart ? (
                                                <>
                                                    <polyline points="4 14 10 14 10 20"></polyline>
                                                    <polyline points="20 10 14 10 14 4"></polyline>
                                                    <line x1="14" y1="10" x2="21" y2="3"></line>
                                                    <line x1="3" y1="21" x2="10" y2="14"></line>
                                                </>
                                            ) : (
                                                <>
                                                    <polyline points="15 3 21 3 21 9"></polyline>
                                                    <polyline points="9 21 3 21 3 15"></polyline>
                                                    <line x1="21" y1="3" x2="14" y2="10"></line>
                                                    <line x1="3" y1="21" x2="10" y2="14"></line>
                                                </>
                                            )}
                                        </svg>
                                    </button>
                                </div>
                                <h3 className="chart-title-svuot">Andamento {appliedTableMode === "bisettimanale" ? "Bisettimanale" : "Settimanale"}: Effettuati vs Target ATERSIR (solo comuni sotto target) <span className="table-subtitle-svuot">(esclusi "Extra")</span></h3>
                                <ReactECharts option={chartOptions} style={{ height: isFullscreenChart ? '80vh' : 400 }} />
                            </div>
                        )}

                        {/* Tabella Dettaglio */}
                        <div className={`table-section-svuot${isFullscreen ? ' fullscreen-table-svuot' : ''}`}>
                            <RotateDevice active={isFullscreen} />
                            <div className="table-section-header-svuot">
                                <h3 className="table-title-svuot">Dettaglio per Comune e Settimana <span className="table-subtitle-svuot">(esclusi "Extra")</span></h3>
                                <div className="toggle-aggregazione-svuot">
                                    <button
                                        className={`toggle-btn-svuot ${!soloSottoTarget ? "active" : ""}`}
                                        onClick={() => setSoloSottoTarget(false)}
                                    >
                                        Tutti
                                    </button>
                                    <button
                                        className={`toggle-btn-svuot ${soloSottoTarget ? "active" : ""}`}
                                        onClick={() => setSoloSottoTarget(true)}
                                    >
                                        Sotto Target
                                    </button>
                                    <div>
                                        <button
                                            className="fullscreen-btn-svuot"
                                            onClick={() => setIsFullscreen(!isFullscreen)}
                                            title={isFullscreen ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                {isFullscreen ? (
                                                    <>
                                                        <polyline points="4 14 10 14 10 20"></polyline>
                                                        <polyline points="20 10 14 10 14 4"></polyline>
                                                        <line x1="14" y1="10" x2="21" y2="3"></line>
                                                        <line x1="3" y1="21" x2="10" y2="14"></line>
                                                    </>
                                                ) : (
                                                    <>
                                                        <polyline points="15 3 21 3 21 9"></polyline>
                                                        <polyline points="9 21 3 21 3 15"></polyline>
                                                        <line x1="21" y1="3" x2="14" y2="10"></line>
                                                        <line x1="3" y1="21" x2="10" y2="14"></line>
                                                    </>
                                                )}
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {tableData.length === 0 && !tableLoading ? (
                                <div className="oper-hr-empty-state">
                                    <div className="oper-hr-empty-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                            <line x1="9" y1="9" x2="15" y2="15"></line>
                                            <line x1="15" y1="9" x2="9" y2="15"></line>
                                        </svg>
                                    </div>
                                    <h4 className="oper-hr-empty-title">Nessun dato in tabella</h4>
                                    <p className="oper-hr-empty-text">Non ci sono dati per i filtri selezionati.</p>
                                </div>
                            ) : (
                                <div className="table-container-svuot" style={{ position: "relative" }}>
                                    {tableLoading && (
                                        <div className="table-loading-overlay-svuot">
                                            <Lf23Loader testo={null} />
                                        </div>
                                    )}
                                    <div className="table-wrapper-svuot">
                                        <table className="table-svuot">
                                            <thead>
                                                <tr>
                                                    <th>SETTIMANA</th>
                                                    <th>MESE</th>
                                                    <th>PROVINCIA</th>
                                                    <th>COMUNE</th>
                                                    <th>FREQUENZA</th>
                                                    <th className="numeric-cell">EFFETTUATI</th>
                                                    <th className="numeric-cell">TARGET ATERSIR</th>
                                                    <th>COPERTURA</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {displayedTableMode === "settimanale" ? (
                                                    tableData.map((row, idx) => {
                                                        const prevRow = idx > 0 ? tableData[idx - 1] : null;
                                                        const showGroupHeader = !prevRow || prevRow.settimana !== row.settimana || prevRow.anno !== row.anno;
                                                        return (
                                                            <React.Fragment key={`sett-${row.anno}-${row.settimana}-${row.comune}`}>
                                                                {showGroupHeader && (
                                                                    <tr className="table-group-header-svuot">
                                                                        <td colSpan={8}>
                                                                            Settimana {row.settimana} - {row.mese} {row.anno}
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                                <tr>
                                                                    <td>S{row.settimana}</td>
                                                                    <td>{row.mese}</td>
                                                                    <td>{row.provincia}</td>
                                                                    <td><strong>{row.comune}</strong></td>
                                                                    <td>
                                                                        <span className="badge-frequenza-svuot">
                                                                            Settimanale
                                                                        </span>
                                                                    </td>
                                                                    <td className="numeric-cell">{row.effettuati}</td>
                                                                    <td className="numeric-cell">{row.targetAtersir}</td>
                                                                    <td>
                                                                        <div className="copertura-cell-svuot">
                                                                            <div className="copertura-bar-svuot">
                                                                                <div
                                                                                    className="copertura-fill-svuot"
                                                                                    style={{
                                                                                        width: `${Math.min(row.coperturaPerc, 100)}%`,
                                                                                        backgroundColor: getCoperturaColor(row.coperturaPerc)
                                                                                    }}
                                                                                ></div>
                                                                            </div>
                                                                            <span className="copertura-value-svuot" style={{ color: getCoperturaColor(row.coperturaPerc) }}>
                                                                                {row.coperturaPerc}%
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            </React.Fragment>
                                                        );
                                                    })
                                                ) : (
                                                    tableData.map((row, idx) => {
                                                        const prevRow = idx > 0 ? tableData[idx - 1] : null;
                                                        const showGroupHeader = !prevRow || prevRow.coppiaSett !== row.coppiaSett || prevRow.anno !== row.anno;
                                                        return (
                                                            <React.Fragment key={`bisett-${row.anno}-${row.coppiaSett}-${row.comune}`}>
                                                                {showGroupHeader && (
                                                                    <tr className="table-group-header-svuot">
                                                                        <td colSpan={8}>
                                                                            Settimane {row.settDa}-{row.settA} - {row.mese} {row.anno}
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                                <tr>
                                                                    <td>S{row.settDa}-S{row.settA}</td>
                                                                    <td>{row.mese}</td>
                                                                    <td>{row.provincia}</td>
                                                                    <td><strong>{row.comune}</strong></td>
                                                                    <td>
                                                                        <span className="badge-frequenza-svuot badge-bisett">
                                                                            Bisettimanale
                                                                        </span>
                                                                    </td>
                                                                    <td className="numeric-cell">{row.effettuati}</td>
                                                                    <td className="numeric-cell">{row.targetAtersir}</td>
                                                                    <td>
                                                                        <div className="copertura-cell-svuot">
                                                                            <div className="copertura-bar-svuot">
                                                                                <div
                                                                                    className="copertura-fill-svuot"
                                                                                    style={{
                                                                                        width: `${Math.min(row.coperturaPerc, 100)}%`,
                                                                                        backgroundColor: getCoperturaColor(row.coperturaPerc)
                                                                                    }}
                                                                                ></div>
                                                                            </div>
                                                                            <span className="copertura-value-svuot" style={{ color: getCoperturaColor(row.coperturaPerc) }}>
                                                                                {row.coperturaPerc}%
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            </React.Fragment>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Paginazione */}
                                    <div className="pagination-svuot">
                                        <button
                                            className="pagination-btn-svuot"
                                            disabled={paginaCorrente === 1}
                                            onClick={() => handlePageChange(paginaCorrente - 1)}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M15 18l-6-6 6-6" />
                                            </svg>
                                            Precedente
                                        </button>
                                        <div className="pagination-info-svuot">
                                            <span className="pagination-text-svuot">
                                                Pagina <strong>{paginaCorrente}</strong> di <strong>{pagineTotali}</strong>
                                            </span>
                                            <span className="pagination-total-svuot">
                                                Totale: {totaleRighe} righe &middot; {totaleComuni} comuni
                                            </span>
                                        </div>
                                        <button
                                            className="pagination-btn-svuot"
                                            disabled={paginaCorrente === pagineTotali || pagineTotali === 0}
                                            onClick={() => handlePageChange(paginaCorrente + 1)}
                                        >
                                            Successiva
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M9 18l6-6-6-6" />
                                            </svg>
                                        </button>

                                        <div className="items-per-page-svuot">
                                            <span className="items-label-svuot">Elementi per pagina:</span>
                                            <div className="items-buttons-svuot">
                                                {[10, 20, 50, 100].map((num) => (
                                                    <button
                                                        key={num}
                                                        className={`items-btn-svuot ${rowsPerPage === num ? "active" : ""}`}
                                                        onClick={() => {
                                                            setRowsPerPage(num);
                                                            setPaginaCorrente(1);
                                                        }}
                                                    >
                                                        {num}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}

            </div>
        </div >
    );

};

export default SvuotamentiSettAtersir;
